import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { format, isFuture } from 'date-fns';
import React, { useEffect, useState } from 'react';
import Button from '@dashboard/components/Button';
import Faq from '@dashboard/components/Faq';
import { Label, Switch, FormSubmit } from '@dashboard/components/Form';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Link from '@dashboard/components/Link';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { formatPrice } from '@dashboard/services/formatting';
import plans, { getBillingPeriod, getPlan } from '@dashboard/services/plans';
import { fetcher } from '@dashboard/services/request';
import { useToast } from '@dashboard/context/ToastContext';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useHistory } from 'react-router-dom';
import ScrollToTop from '@/shared/components/ScrollToTop';
import { sellerKey } from '@dashboard/lib/queryKeys';
import Callout from '@dashboard/components/Callout';
import { stringify } from 'query-string';
import getCouponDiscount from './getCouponDiscount';
import useRewardful from '@dashboard/hooks/useRewardful';
import Description from '@dashboard/components/Description';

const availablePlans = plans.filter(plan => plan.available);

const Plan = ({
  handle,
  version,
  billingPeriod = 'month',
  discount,
  selected,
  setSelectedPlanHandle,
  isCurrentPlan,
}) => {
  const plan = getPlan(handle, version);

  if (!plan) return null;

  return (
    <button
      className={clsx('rounded-lg border-2 w-full text-left p-4', {
        'border-gray-200': !selected,
        'border-orange-500': selected,
        'bg-gray-50 border-transparent': isCurrentPlan,
      })}
      onClick={() => setSelectedPlanHandle(plan.handle)}
      disabled={isCurrentPlan}
    >
      <div className="flex justify-between flex-start">
        <div className="text-left">
          <div className="font-bold mb-1">
            {plan.title}

            {plan.available ? (
              ''
            ) : (
              <span className="ml-1 text-gray-700 font-normal">
                {'('}legacy{')'}
              </span>
            )}
          </div>

          <div className="text-gray-700 font-medium">
            {plan.benefits.map((benefit, index) => (
              <span key={index}>
                {benefit}

                {index < plan.benefits.length - 1 ? <span className="mx-1">•</span> : ''}
              </span>
            ))}
          </div>
        </div>

        <div className="text-right w-52">
          <div className="text-gray-700 font-medium mb-1 whitespace-nowrap">
            {billingPeriod === 'month' && (
              <>
                <span className="text-black font-bold">
                  {isCurrentPlan && discount
                    ? `${formatPrice({
                        amount:
                          plan.monthlyPrice -
                          getCouponDiscount(plan.monthlyPrice, {
                            amountOff: discount.amountOff,
                            percentOff: discount.percentOff,
                          }),
                        currency: 'USD',
                      })} / month`
                    : `${formatPrice({
                        amount: plan.monthlyPrice,
                        currency: 'USD',
                      })} / month`}
                </span>{' '}
              </>
            )}

            {billingPeriod === 'year' && (
              <>
                <span className="text-black font-bold">
                  <span className="line-through text-gray-600 mr-2">
                    {formatPrice({
                      amount: plan.monthlyPrice,
                      currency: 'USD',
                    })}{' '}
                    / month
                  </span>

                  {isCurrentPlan && discount ? (
                    <>
                      {formatPrice({
                        amount:
                          plan.yearlyPrice -
                          getCouponDiscount(plan.yearlyPrice, {
                            amountOff: discount.amountOff,
                            percentOff: discount.percentOff,
                          }),
                        currency: 'USD',
                      })}{' '}
                      / month
                    </>
                  ) : (
                    <>
                      {formatPrice({
                        amount: plan.yearlyPrice,
                        currency: 'USD',
                      })}{' '}
                      / month
                    </>
                  )}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

const PlanOverview = () => {
  const history = useHistory();
  const {
    plan: sellerPlan,
    seller,
    isCanceled,
    isTrialing,
    isCanceling,
    isTrialExpired,
    token,
    sellerId,
  } = useAuth();
  const urlQuery = useUrlQuery();
  const { billing_period, plan: planQuery, coupon: couponQuery } = urlQuery;
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState(
    billing_period || seller.planBillingPeriod || 'year',
  );
  const [selectedPlanHandle, setSelectedPlanHandle] = React.useState(
    planQuery || (isTrialing || isCanceling || isCanceled || isTrialExpired ? 'grow' : null),
  );

  const { coupon: rewardfulCoupon } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponQuery, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponQuery || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponQuery || !!rewardfulCoupon),
    retry: false,
  });

  const currentPlan = getPlan(sellerPlan.handle, sellerPlan.version) || {};
  const selectedPlan = getPlan(selectedPlanHandle);

  useEffect(() => {
    if (planQuery) {
      setSelectedPlanHandle(planQuery);
    }
  }, [planQuery]);

  useEffect(() => {
    if (!seller._id) return; // still loading

    if (selectedPlanHandle) return;

    if (planQuery) return;

    if (seller.subscriptionStatus === 'legacy') return;

    const preselectPlan = availablePlans.find(
      plan => plan.handle === currentPlan.handle && plan.version === currentPlan.version,
    );

    if (preselectPlan) {
      setSelectedPlanHandle(preselectPlan.handle);
    } else {
      setSelectedPlanHandle('grow');
    }
  }, [seller, currentPlan]);

  // set billing period from sellerPlan
  useEffect(() => {
    if (billing_period) return;

    if (seller.planBillingPeriod) {
      setSelectedBillingPeriod(seller.planBillingPeriod);

      history.push({
        search: stringify({
          ...urlQuery,
          billing_period: seller.planBillingPeriod,
        }),
      });
    }
  }, [seller]);

  // set coupon from sellerPlan
  useEffect(() => {
    if (couponQuery) return;

    if (seller.stripeSubscriptionDiscount) {
      history.push({
        search: stringify({
          ...urlQuery,
          coupon: seller.stripeSubscriptionDiscount.promotionCode,
        }),
      });
    }
  }, [seller]);

  function toggleBillingPeriod() {
    setSelectedBillingPeriod(selectedBillingPeriod === 'month' ? 'year' : 'month');

    history.push({
      search: stringify({
        ...urlQuery,
        billing_period: selectedBillingPeriod === 'month' ? 'year' : 'month',
      }),
    });
  }

  const hasSubscription = seller.subscriptionStatus === 'active';

  const sellerLosesCustomDomain =
    seller.customDomain && seller.customDomainVerified && selectedPlanHandle === 'launch';

  const monthlyCouponDiscount = getCouponDiscount(selectedPlan?.monthlyPrice, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const yearlyCouponDiscount = getCouponDiscount(selectedPlan?.yearlyPrice * 12, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const discountedMonthlyPrice = selectedPlan?.monthlyPrice - monthlyCouponDiscount;

  const yearlyPrice = selectedPlan?.yearlyPrice * 12;
  const discountedYearlyPrice = yearlyPrice - yearlyCouponDiscount;

  return (
    <>
      <ScrollToTop />

      <SettingsContainer size="2xl">
        {currentPlan.handle && (
          <div>
            <div>
              {hasSubscription && (
                <div className="mb-8">
                  <SettingsHeader>Current plan</SettingsHeader>

                  <div className="mb-2">
                    <PlanStatus plan={currentPlan} />
                  </div>

                  <Plan
                    handle={currentPlan.handle}
                    version={currentPlan.version}
                    billingPeriod={seller.planBillingPeriod}
                    discount={seller.stripeSubscriptionDiscount}
                    setSelectedPlanHandle={() => null}
                    isCurrentPlan
                  />
                </div>
              )}

              {!isTrialing && !hasSubscription && (
                <div className="mb-8">
                  <PlanStatus plan={currentPlan} />
                </div>
              )}

              <div className="">
                <SettingsHeader>
                  {isTrialing || isTrialExpired
                    ? 'Start subscription'
                    : seller.subscriptionStatus === 'active'
                    ? 'Change plan'
                    : 'Upgrade plan'}
                </SettingsHeader>

                <div className="flex justify-between mt-3 mb-3">
                  <div>
                    <Link target="blank" to="/pricing">
                      Compare pricing plans{' '}
                      <InformationCircleIcon className="inline-block w-4 h-4" />
                    </Link>
                  </div>

                  <div className="flex items-center">
                    <Switch
                      onChange={toggleBillingPeriod}
                      value={selectedBillingPeriod === 'year'}
                      id="billingPeriod"
                    />
                    <Label
                      className="ml-2 cursor-pointer !mb-0 !text-base font-medium !text-gray-900"
                      htmlFor="billingPeriod"
                    >
                      Yearly billing (2 months free)
                    </Label>
                  </div>
                </div>

                <div className="space-y-3">
                  {availablePlans
                    .filter(() => {
                      if (isTrialing) return true;
                      if (isCanceling) return true;

                      if (selectedBillingPeriod !== seller.planBillingPeriod) return true;

                      return true;
                    })
                    .map(plan => (
                      <Plan
                        key={plan.handle}
                        handle={plan.handle}
                        version={plan.version}
                        billingPeriod={selectedBillingPeriod}
                        selected={selectedPlan && selectedPlan.handle === plan.handle}
                        setSelectedPlanHandle={setSelectedPlanHandle}
                      />
                    ))}
                </div>

                {sellerLosesCustomDomain && (
                  <div className="mt-4">
                    <Callout variant="warning">
                      You’ll lose your custom domain. Subscribe to Grow or Scale to keep your custom
                      domain.
                    </Callout>
                  </div>
                )}

                {coupon && (
                  <div className="flex justify-end mt-2">
                    <div className="bg-orange-50 px-4 py-4 space-y-1 text-right rounded">
                      {rewardfulCoupon && (
                        <p>
                          <span className="font-semibold">{rewardfulCoupon.name}</span>
                        </p>
                      )}

                      <p>
                        <span className="font-semibold">Discount</span>:{' '}
                        {formatPrice({
                          amount:
                            selectedBillingPeriod === 'year'
                              ? yearlyCouponDiscount
                              : monthlyCouponDiscount,
                          currency: 'USD',
                        })}
                        <>
                          {coupon.duration === 'once' && ' (one-time)'}

                          {coupon.duration === 'forever' && ` / ${selectedBillingPeriod}`}

                          {coupon.duration === 'repeating' &&
                            ` (${coupon.duration_in_months} months)`}
                        </>
                      </p>

                      <p>
                        <span className="font-semibold">Total</span>:{' '}
                        {formatPrice({
                          amount:
                            selectedBillingPeriod === 'year'
                              ? discountedYearlyPrice
                              : discountedMonthlyPrice,
                          currency: 'USD',
                        })}{' '}
                        / {selectedBillingPeriod}
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  {selectedPlan && (
                    <SubscriptionButton
                      plan={selectedPlan}
                      billingPeriod={selectedBillingPeriod}
                      setSelectedPlanHandle={setSelectedPlanHandle}
                      upgradePath="/billing/plan/upgrade"
                      upgradeFlow="billing-settings"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </SettingsContainer>

      <div className="mt-16">
        <SettingsContainer size="2xl">
          <SettingsHeader>Frequently asked questions</SettingsHeader>

          <div className="mx-auto max-w-2xl">
            <Faq>
              <Faq.Item
                question="What happens when my 7-day free trial ends?"
                answer="To use Checkout Page, you need to have an active subscription. If you decide not to subscribe before your trial ends, your pages will no longer work and you'll lose access to your dashboard."
              />

              <Faq.Item
                question="Are there any limits to the free 7-day trial?"
                answer="During your free 7-day trial, you can process up to $500 in payments. If you exceed this limit, you'll need to subscribe to one of our monthly plans to continue accepting payments.
                <br /><br />
                To prevent interruptions to your payments, please start a subscription during your trial if you expect to exceed this limit."
              />

              <Faq.Item
                question="What happens if I exceed the sales per month limit?"
                answer={
                  <>
                    <p className="mb-4">
                      If you exceed the sales per month limit, we'll automatically upgrade you to
                      the next suitable plan.
                    </p>
                    <p>
                      For detailed information about overages and plan upgrades,{' '}
                      <Link href="/help/articles/pricing-fees" target="blank" variant="secondary">
                        visit our pricing guide
                      </Link>
                      .
                    </p>
                  </>
                }
              />

              <Faq.Item
                question="How do I cancel my subscription?"
                answer={
                  <>
                    <div className="mb-4">
                      You can cancel your subscription anytime by clicking "Cancel subscription".
                      When you cancel your subscription, you can use Checkout Page until the end of
                      your billing period.
                    </div>

                    <div>
                      <div className="flex justify-between mt-1.5">
                        <Button appearance="secondary" to={`/billing/plan/cancel`}>
                          Cancel subscription
                        </Button>
                      </div>
                    </div>
                  </>
                }
              />

              <Faq.Item
                question="How do I change my plan?"
                answer="You can change your plan anytime. Upgrades take effect immediately and you'll be charged the difference between the old and new plan. Downgrades take effect at the end of the current billing period."
              />

              <Faq.Item
                question="How do I change my billing period?"
                answer="You can change your subscription from monthly to yearly or vice versa at any time. If you switch from monthly to yearly, you'll be charged the difference immediately. To switch from yearly to monthly, please contact us and we'll make the change for you. Either way, you'll keep your current plan until the end of your billing period."
              />
            </Faq>
          </div>
        </SettingsContainer>
      </div>

      {hasSubscription && !seller.stripeSubscriptionCancelAt && (
        <div className="mt-16">
          <SettingsContainer size="2xl">
            <SettingsHeader>Cancel subscription</SettingsHeader>
            <div className="flex justify-between mt-1.5">
              <Button appearance="secondary" to={`/billing/plan/cancel`}>
                Cancel subscription
              </Button>
            </div>
          </SettingsContainer>
        </div>
      )}
    </>
  );
};

function PlanStatus({ plan }) {
  const { isTrialing, plan: sellerPlan, seller } = useAuth();

  const hasActiveSubscription = seller.subscriptionStatus === 'active';
  const currentPlanHandle = sellerPlan.handle;

  if (!hasActiveSubscription || isTrialing) {
    return null;
  }

  if (currentPlanHandle === plan.handle) {
    return <div className="text-gray-700 font-medium">{getRenewalStatus(seller)}</div>;
  }

  return null;
}

function getRenewalStatus(seller) {
  const trialEndsAt = seller.trialEnd || null;
  const renewsAt = seller.stripeSubscriptionCurrentPeriodEnd || null;
  const cancelsAt = seller.stripeSubscriptionCancelAt || null;
  const hasPendingPlanChange = seller.pendingPlanChange?.stripeSubscriptionScheduleId;

  // pendin plan changes are always downgrades
  if (hasPendingPlanChange) {
    return `Downgrades to ${seller.pendingPlanChange.plan.title} on ${format(
      new Date(seller.pendingPlanChange.effectiveDate),
      'd MMM, yyyy',
    )}`;
  }

  if (cancelsAt) {
    return `Cancels on ${format(new Date(cancelsAt), 'd MMM, yyyy')}`;
  }

  if (trialEndsAt && isFuture(new Date(trialEndsAt))) {
    return `Trialing, first payment on ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
  }

  return `Renews on ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
}

export function SubscriptionButton({
  plan = {},
  billingPeriod,
  setSelectedPlanHandle,
  upgradePath = '/billing/plan/upgrade',
  upgradeFlow,
}) {
  const { seller, sellerId, plan: sellerPlan, token, setSeller } = useAuth();
  const [error, setError] = useState(null);
  const { emitToast } = useToast();
  const { coupon: couponQuery } = useUrlQuery();

  const pendingPlan = seller.pendingPlanChange?.plan?.handle
    ? getPlan(seller.pendingPlanChange?.plan?.handle, seller.pendingPlanChange?.plan?.version)
    : null;

  const currentPlan = pendingPlan || getPlan(sellerPlan.handle, sellerPlan.version) || {};

  const { data: updatedSeller, refetch: refetchSeller } = useQuery({
    queryKey: sellerKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}`, token),
  });

  const changePlanMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/change-subscription`, token, {
        method: 'POST',
        body: JSON.stringify({
          handle: plan.handle,
          billingPeriod,
          coupon: couponQuery,
          upgradeFlow,
        }),
      }),
    onSuccess: async () => {
      const { data: updatedSeller } = await refetchSeller();

      if (updatedSeller.pendingPlanChange) {
        emitToast({
          title: 'Subscription updated',
          description: `Your subscription will downgrade to ${
            updatedSeller.pendingPlanChange.plan.title
          } on ${format(new Date(updatedSeller.pendingPlanChange.effectiveDate), 'd MMM, yyyy')}`,
          type: 'success',
        });
      } else {
        emitToast({
          title: 'Subscription updated',
          description: `You're now on the ${plan.title} plan`,
          type: 'success',
        });
      }

      setSelectedPlanHandle(null);
    },
    onError: error => {
      setError(error.message);
    },
  });

  useEffect(() => {
    if (!updatedSeller) return;

    setSeller(updatedSeller);
  }, [updatedSeller]);

  useEffect(() => {
    if (!updatedSeller) return;

    // if updatedSeller has a different plan, set selected plan to null
    if (updatedSeller && updatedSeller.plan?.handle !== currentPlan.handle) {
      setSelectedPlanHandle(updatedSeller.plan?.handle);
    }
  }, [currentPlan, updatedSeller]);

  const isLegacyPremium =
    seller.subscriptionStatus === 'legacy' && currentPlan.handle === 'premium';

  const canStartSubscription =
    seller.subscriptionStatus === 'legacy' ||
    seller.subscriptionStatus === 'trialing' ||
    seller.subscriptionStatus === 'canceled' ||
    seller.subscriptionStatus === 'trial_expired' ||
    seller.stripeSubscriptionCancelAt;

  if (canStartSubscription) {
    return (
      <Button
        appearance="primary"
        size="full"
        to={`${upgradePath}?${stringify({
          plan: plan.handle,
          billing_period: billingPeriod,
          coupon: couponQuery,
          upgradeFlow,
        })}`}
      >
        <a className="btn btn-primary">Start subscription</a>
      </Button>
    );
  }

  const hasSubscription = seller.subscriptionStatus === 'active' || isLegacyPremium;

  if (hasSubscription) {
    if (seller.planBillingPeriod !== billingPeriod) {
      const canUpgrade = seller.planBillingPeriod === 'month';

      if (canUpgrade) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="primary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              Upgrade to yearly billing
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="flex flex-wrap gap-2">
          <Button
            size="full"
            onClick={async () => {
              if (!window.$crisp) return;

              window.$crisp.push([
                'set',
                'message:text',
                [
                  `Hi! I want to change from ${currentPlan.title} - ${getBillingPeriod(
                    seller.planBillingPeriod,
                  )} to ${plan.title} - ${getBillingPeriod(billingPeriod)}.`,
                ],
              ]);

              window.setTimeout(() => {
                window.$crisp.push(['do', 'chat:open']);
              }, 300);
            }}
            appearance="secondary"
          >
            Contact us to change to monthly
          </Button>
        </div>
      );
    } else {
      const isSamePlan = currentPlan.handle === plan.handle;
      const isVersionChange = currentPlan.version !== plan.version;

      if (isSamePlan && isVersionChange) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="primary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              Change to {plan.title}
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}
          </div>
        );
      }

      const isDowngrade = plan.monthlyPrice < currentPlan.monthlyPrice;

      if (isDowngrade) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="secondary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              Downgrade to {plan.title}
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}

            <Description className="mt-2 text-center">
              Downgrades take effect at the end of your current billing period.
            </Description>
          </div>
        );
      }

      if (isSamePlan) {
        return null;
      }

      return (
        <div>
          <FormSubmit.Button
            size="full"
            appearance="primary"
            isSubmitting={changePlanMutation.isLoading}
            hasError={!!error}
            onClick={async () => {
              setError(null);
              changePlanMutation.mutate();
            }}
            disabled={changePlanMutation.isLoading}
          >
            Upgrade to {plan.title}
          </FormSubmit.Button>

          {error && (
            <div className="mt-2">
              <FormErrorBase>{error}</FormErrorBase>
            </div>
          )}
        </div>
      );
    }
  }

  return '';
}

export default PlanOverview;
